import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { AuthProvider } from './context/AuthContext';
import { AcademyProvider } from './context/AcademyContext';
import { DashboardProvider } from './context/DashboardContext';
import { DialogProvider } from './context/DialogContext';
import GlobalStyle from './styles/GlobalStyle';
import theme from './styles/theme';

// Guards
import RequireAuth from './components/guards/RequireAuth';
import RequireAdmin from './components/guards/RequireAdmin';

// System / Auth pages
import SplashPage from './pages/System/SplashPage';
import LinkPhonePage from './pages/Auth/LinkPhonePage';
import PendingPage from './pages/Auth/PendingPage';
import FindIdPage from './pages/Auth/FindIdPage';
import FindPasswordPage from './pages/Auth/FindPasswordPage';
import CompleteProfilePage from './pages/Auth/CompleteProfilePage';
import AcademyApplyPage from './pages/Academy/AcademyApplyPage';
import AcademyApplyEditPage from './pages/Academy/AcademyApplyEditPage';
import AcademyFindPage from './pages/Academy/AcademyFindPage';
import AppShell from './components/layout/AppShell';
import MyAcademiesPage from './pages/App/MyAcademiesPage';
import MyPage from './pages/App/MyPage';
import StubPage from './pages/App/StubPage';
import AcademyMapPage from './pages/App/AcademyMapPage';
import DemoChoicePage from './pages/App/DemoChoicePage';
import AppHomePage from './pages/App/AppHomePage';
import AppChatListPage from './pages/App/AppChatListPage';
import AcademyManagePage from './pages/App/AcademyManagePage';
import BizHomePage from './pages/App/BizHomePage';
import BizStudentsPage from './pages/App/BizStudentsPage';
import BizClassesPage from './pages/App/BizClassesPage';
import BizPaymentPage from './pages/App/BizPaymentPage';
import BizClassDetailPage from './pages/App/BizClassDetailPage';
import BizSchedulePage from './pages/App/BizSchedulePage';
import AcademyDetailPage from './pages/App/AcademyDetailPage';
import AcademyPhotosPage from './pages/App/AcademyPhotosPage';
import AppLoginPage from './pages/App/AppLoginPage';
import AccountTypeSelectPage from './pages/App/AccountTypeSelectPage';
import BizSetupPage from './pages/App/BizSetupPage';
import EmailAuthPage from './pages/App/EmailAuthPage';
import KakaoCallbackPage from './pages/App/KakaoCallbackPage';
import AcademyChatPage from './pages/App/AcademyChatPage';
import MemberAcademyPage from './pages/App/MemberAcademyPage';
import AppTermsPage from './pages/App/AppTermsPage';
import AppPrivacyPage from './pages/App/AppPrivacyPage';
import AccountDeletionPage from './pages/App/AccountDeletionPage';
import DevAccountsPage from './pages/Dev/DevAccountsPage';
import KioskPage from './pages/Kiosk/KioskPage';
import DemoIntroPage from './pages/Demo/DemoIntroPage';
import LandingPage from './pages/Landing/LandingPage';
import DownloadRedirectPage from './pages/System/DownloadRedirectPage';

// Login / SignUp
import SignUpPage from './pages/Login/SignUpPage';
import AuthLoadingPage from './pages/Login/AuthLoadingPage';
import PhoneVerifyPage from './pages/Login/PhoneVerifyPage';

// 학원장 PC 콘솔 (데스크탑 전용)
import ConsoleLayout from './components/console/ConsoleLayout';
import ConsoleDashboard from './pages/Console/ConsoleDashboard';
import ConsoleClassesPage from './pages/Console/ConsoleClassesPage';
import ConsoleStudentsPage from './pages/Console/ConsoleStudentsPage';
import ConsoleSchedulePage from './pages/Console/ConsoleSchedulePage';
import ConsoleSettingsPage from './pages/Console/ConsoleSettingsPage';

// 운영자(최고관리자) 콘솔 — 플랫폼 전체 god-mode
import OperatorLayout from './components/operator/OperatorLayout';
import OperatorDashboard from './pages/Operator/OperatorDashboard';
import OperatorAcademiesPage from './pages/Operator/OperatorAcademiesPage';
import OperatorMembersPage from './pages/Operator/OperatorMembersPage';

// Admin
import AdminLoginPage from './pages/Admin/Login/AdminLoginPage';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/Admin/Dashboard/AdminDashboard';
import AdminUsersPage from './pages/Admin/Users/AdminUsersPage';
import AdminClassesPage from './pages/Admin/Classes/AdminClassesPage';
import AdminClassDetailPage from './pages/Admin/Classes/AdminClassDetailPage';
import AdminSchedulePage from './pages/Admin/Schedule/AdminSchedulePage';
import AdminReportsPage from './pages/Admin/Reports/AdminReportsPage';
import AdminMessagesPage from './pages/Admin/Messages/AdminMessagesPage';
import AdminSettingsPage from './pages/Admin/Settings/AdminSettingsPage';
import AdminMaterialsPage from './pages/Admin/Materials/AdminMaterialsPage';
import AdminUpdatesPage from './pages/Admin/Updates/AdminUpdatesPage';
import AdminNotificationsPage from './pages/Admin/Notifications/AdminNotificationsPage';
import useKeyboardScroll from './hooks/useKeyboardScroll';
import { checkAppUpdate } from './services/appVersionService';

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function VersionChecker() {
  const [toast, setToast] = React.useState('');
  React.useEffect(() => {
    checkAppUpdate({ onUpdate: (content) => setToast(content) });
  }, []);
  if (!toast) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 100, left: '50%', transform: 'translateX(-50%)',
      background: '#fff', padding: '14px 20px', borderRadius: 12,
      fontSize: 14, zIndex: 9999, display: 'flex', alignItems: 'center', gap: 10,
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: 270, maxWidth: 340,
    }}>
      <span style={{ fontSize: 18, color: 'var(--color-primary)' }}>&#9432;</span>
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}>새로운 버전으로 업데이트 중...</div>
        <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>{toast}</div>
      </div>
    </div>
  );
}

function App() {
  useKeyboardScroll();

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AuthProvider>
        <AcademyProvider>
        <DashboardProvider>
        <DialogProvider>
        <Router>
          <ScrollToTop />
          <VersionChecker />
          <Routes>
            {/* 스플래시 (웹 진입점 + WEB_READY + 분기) */}
            <Route path="/" element={<SplashPage />} />

            {/* 공개 라우트 */}
            <Route path="/intro" element={<LandingPage />} />
            <Route path="/dl" element={<DownloadRedirectPage />} />
            <Route path="/account-deletion" element={<AccountDeletionPage />} />
            {/* 옛 ClassCare 로그인 화면 제거 → 카카오 로그인으로 리다이렉트.
                (/login 참조가 곳곳에 남아 있어 라우트는 유지하되 화면만 교체) */}
            <Route path="/login" element={<Navigate to="/app/login" replace />} />
            <Route path="/app/login" element={<AppLoginPage />} />
            <Route path="/app/email-login" element={<EmailAuthPage />} />
            <Route path="/oauth/kakao" element={<KakaoCallbackPage />} />
            <Route path="/dev/accounts" element={<DevAccountsPage />} />
            <Route path="/kiosk/:aid" element={<KioskPage />} />
            <Route path="/demo" element={<DemoChoicePage />} />
            <Route path="/demo/:role" element={<DemoIntroPage />} />

            {/* 1등급 MVP 앱 — 둘러보기(비로그인)도 접근 가능 */}
            <Route path="/app" element={<AppShell />}>
              <Route path="home" element={<AppHomePage />} />
              <Route path="my" element={<MyAcademiesPage />} />
              <Route path="map" element={<AcademyMapPage />} />
              <Route path="chat" element={<AppChatListPage />} />
              {/* 커뮤니티(UGC) 비활성화 — App Store 1.2 대응. 추후 신고/차단/약관 갖춰 재오픈 */}
              <Route path="community" element={<Navigate to="/app/home" replace />} />
              <Route path="search" element={<StubPage title="검색" />} />
              <Route path="notifications" element={<StubPage title="공지" />} />
              <Route path="profile" element={<MyPage />} />
              <Route path="biz" element={<BizHomePage />} />
              <Route path="biz/classes" element={<BizClassesPage />} />
              <Route path="biz/schedule" element={<BizSchedulePage />} />
              <Route path="biz/students" element={<BizStudentsPage />} />
              <Route path="biz/pay" element={<BizPaymentPage />} />
              <Route path="biz/chat" element={<StubPage title="학부모 채팅" />} />
              <Route path="academy/:id" element={<AcademyDetailPage />} />
              <Route path="academy/:id/photos" element={<AcademyPhotosPage />} />
            </Route>
            <Route path="/app/account-type" element={<AccountTypeSelectPage />} />
            <Route path="/app/biz/start" element={<BizSetupPage />} />
            <Route path="/app/terms/:role" element={<AppTermsPage />} />
            <Route path="/app/privacy/:role" element={<AppPrivacyPage />} />
            <Route path="/app/member/:id" element={<MemberAcademyPage />} />
            <Route path="/app/chat/:id" element={<AcademyChatPage />} />
            <Route path="/app/biz/classes/:cid" element={<BizClassDetailPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/auth-loading" element={<AuthLoadingPage />} />
            <Route path="/phone-verify" element={<PhoneVerifyPage />} />
            <Route path="/find-id" element={<FindIdPage />} />
            <Route path="/find-password" element={<FindPasswordPage />} />

            {/* RequireAuth만 (RequirePhone/RequireApproval 밖) */}
            <Route element={<RequireAuth />}>
              <Route path="/complete-profile" element={<CompleteProfilePage />} />
              <Route path="/academy/register" element={<AcademyApplyPage />} />
              <Route path="/academy/apply" element={<AcademyApplyPage />} />
              <Route path="/academy/apply/edit/:field" element={<AcademyApplyEditPage />} />
              <Route path="/academy/find" element={<AcademyFindPage />} />
              <Route path="/app/manage/:id" element={<AcademyManagePage />} />

              <Route path="/link-phone" element={<LinkPhonePage />} />
              <Route path="/pending" element={<PendingPage />} />
            </Route>

            {/* 학원장 PC 콘솔 — 큰 화면에서 학원 운영 (academyService 멀티테넌트) */}
            <Route path="/console" element={<ConsoleLayout />}>
              <Route index element={<ConsoleDashboard />} />
              <Route path="classes" element={<ConsoleClassesPage />} />
              <Route path="students" element={<ConsoleStudentsPage />} />
              <Route path="schedule" element={<ConsoleSchedulePage />} />
              <Route path="settings" element={<ConsoleSettingsPage />} />
            </Route>

            {/* 운영자(최고관리자) 콘솔 — 전체 학원·회원 관리 + 학원 등록(디렉토리 시딩) */}
            <Route path="/operator" element={<OperatorLayout />}>
              <Route index element={<OperatorDashboard />} />
              <Route path="academies" element={<OperatorAcademiesPage />} />
              <Route path="members" element={<OperatorMembersPage />} />
            </Route>

            {/* 관리자 */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route element={<RequireAdmin />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="classes" element={<AdminClassesPage />} />
                <Route path="classes/:id" element={<AdminClassDetailPage />} />
                <Route path="materials" element={<AdminMaterialsPage />} />
                <Route path="schedule" element={<AdminSchedulePage />} />
                <Route path="reports" element={<AdminReportsPage />} />
                <Route path="messages" element={<AdminMessagesPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
                <Route path="updates" element={<AdminUpdatesPage />} />
                <Route path="notifications" element={<AdminNotificationsPage />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
        </DialogProvider>
        </DashboardProvider>
        </AcademyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
